import {
  Alert,
  Badge,
  Card,
  ProgressBar,
  SkeletonLoader,
  Title,
} from '@yoast/ui-library';
import cx from 'classnames';
import type React from 'react';

import type {
  SentimentScore,
  SentimentScoresTopCompetitor,
} from '../api/generated/insights.schemas';
import TextScore from './TextScore';
import TitleWithTooltip from './TitleWithTooltip';

type KeywordBadgeListProps = {
  keywords: string[];
  badgeClassName: string;
};

const KeywordBadgeList = ({
  keywords,
  badgeClassName,
}: KeywordBadgeListProps) => (
  <div className="yst-flex yst-flex-wrap yst-gap-2">
    {keywords.map(
      (keyword, index) =>
        keyword && (
          <Badge key={index} className={badgeClassName}>
            {keyword}
          </Badge>
        ),
    )}
  </div>
);

type SentimentCardProps = {
  title?: string;
  // brandName?: string;
  data?: SentimentScore | SentimentScoresTopCompetitor;
  tooltip?: string | React.ReactNode;
};

const SentimentCard = ({
  title = 'Sentiment',
  tooltip = '',
  // brandName = '',
  data,
}: SentimentCardProps) => {
  const brandName = data?.brandName;
  const sentimentPct = data?.positivityPercentage?.value;
  const sentimentChangePct = data?.positivityPercentage?.previous?.difference;

  const keywordsPositive = data?.keywords?.positive;
  const keywordsNegative = data?.keywords?.negative;

  // Check if we have no sentiment data to show empty state
  const hasNoData =
    !data || (sentimentPct === undefined && sentimentChangePct === undefined);

  return (
    <Card className="yst-overflow-visible">
      <Card.Content className="yst-space-y-8">
        <TitleWithTooltip title={title} tooltip={tooltip} />
        <div className="yst-space-y-3">
          {!hasNoData && (
            <>
              <p>
                {brandName || <SkeletonLoader className="yst-h-6 yst-w-20" />}
              </p>

              <div>
                <div className="yst-inline-flex">
                  <TextScore
                    className="yst-mb-1.5"
                    score={sentimentPct}
                    scoreFormat="percentage"
                    suffix="positive"
                    delta={sentimentChangePct}
                  />
                </div>

                <ProgressBar
                  max={100}
                  min={0}
                  progress={sentimentPct || 0}
                  className={cx(
                    {
                      // wait to set bg until data loads
                      'yst-bg-red-500': sentimentPct,
                    },
                    // set progress bar to green
                    '[&>*]:yst-bg-green-500',
                  )}
                />
              </div>
            </>
          )}
        </div>

        {hasNoData && (
          <Alert variant="info">
            The selected AI model returned no brand sentiment data for your
            queries. This can happen with some queries or models.
          </Alert>
        )}

        {!!keywordsPositive && (
          <div className="yst-space-y-2">
            <Title as="h5" size="5">
              Positive keywords
            </Title>
            <KeywordBadgeList
              keywords={keywordsPositive}
              badgeClassName="yst-bg-green-100 yst-text-green-800"
            />
          </div>
        )}

        {!!keywordsNegative && (
          <div className="yst-space-y-2">
            <Title as="h5" size="5">
              Negative keywords
            </Title>
            <KeywordBadgeList
              keywords={keywordsNegative}
              badgeClassName="yst-bg-red-100 yst-text-red-800"
            />
          </div>
        )}
      </Card.Content>
    </Card>
  );
};

export default SentimentCard;
